import React, { useState, useEffect } from "react";
import {
	Box,
	Typography,
	Card,
	CardContent,
	CardMedia,
	Grid,
	Skeleton,
	Button,
	Stack,
	LinearProgress,
	TextField,
	InputAdornment,
	Select,
	MenuItem,
} from "@mui/material";
import { Play, BookOpen, Search } from "lucide-react";
import { supabase } from "../../config/supabase";
import { useGrade } from "../../contexts/GradeContext";
import { useAuth } from "../../contexts/AuthContext";
import { useAcademicYear } from "../../contexts/AcademicYearContext";
import { useNavigate } from "react-router-dom";
import { useMediaQuery, useTheme } from "@mui/material";

const STATUS_OPTIONS = [
	{ value: "all", label: "All Courses" },
	{ value: "not_started", label: "Not Started" },
	{ value: "in_progress", label: "In Progress" },
	{ value: "completed", label: "Completed" },
];

const FunmathGames = () => {
	const { selectedGrade } = useGrade();
	const { user } = useAuth();
	const { currentAcademicYear } = useAcademicYear();
	const [categories, setCategories] = useState([]);
	const [courseProgress, setCourseProgress] = useState({});
	const [loading, setLoading] = useState(true);
	const [searchQuery, setSearchQuery] = useState("");
	const [statusFilter, setStatusFilter] = useState("all");
	const navigate = useNavigate();

	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

	useEffect(() => {
		const fetchData = async () => {
			if (!selectedGrade || !user) {
				setLoading(false);
				return;
			}

			setLoading(true);
			try {
				// Updated query to use category_grades joining table
				const { data: categoryData, error: categoryError } = await supabase
					.from("category_grades")
					.select(
						`
						category_id,
						course_categories (
							id,
							name,
							description,
							thumbnail,
							concept,
							status
						)
					`
					)
					.eq("grade_id", selectedGrade);

				if (categoryError) throw categoryError;

				// Transform the data to get the categories
				const transformedCategories = categoryData
					?.map((item) => item.course_categories)
					?.filter((category) => category && category.status !== false); // Filter out disabled categories


				// FIXED: Get progress for the specific grade being viewed
				const { data: progressData, error: progressError } = await supabase
					.from("teacher_course_progress")
					.select("*")
					.eq("teacher_id", user.id)
					.eq("grade_id", selectedGrade)
					.eq("academic_year", currentAcademicYear); // This was the issue - need to filter by grade_id

				if (progressError) throw progressError;


				// NEW: Calculate completion percentage based on actual content progress
				const progressWithCalculatedCompletion = await Promise.all(
					(progressData || []).map(async (courseProgress) => {
						try {
							// Get all contents for this category
							const { data: contents, error: contentsError } = await supabase
								.from("course_contents")
								.select("id")
								.eq("category_id", courseProgress.category_id)
								.eq("status", true); // Only count active contents

							if (contentsError) {
								console.error("Error fetching contents:", contentsError);
								return courseProgress;
							}

							// Get UNIQUE completed content progress for this category (avoid counting duplicates)
							const { data: contentProgress, error: contentProgressError } =
								await supabase
									.from("teacher_content_progress")
									.select("content_id, status")
									.eq("teacher_id", user.id)
									.eq("category_id", courseProgress.category_id)
									.eq("status", "completed")
									.eq("academic_year", currentAcademicYear);

							if (contentProgressError) {
								console.error(
									"Error fetching content progress:",
									contentProgressError
								);
								return courseProgress;
							}

							// FIXED: Remove duplicates by using Set to get unique content_ids
							const uniqueCompletedContentIds = new Set(
								contentProgress.map((cp) => cp.content_id)
							);

							const totalContents = contents.length || 1;
							const completedContents = uniqueCompletedContentIds.size; // Use size of Set for unique count
							const actualCompletionPercentage = Math.min(
								100,
								Math.round((completedContents / totalContents) * 100)
							); // Cap at 100%

							

							// Return updated progress with calculated completion
							return {
								...courseProgress,
								completion_percentage: actualCompletionPercentage,
								calculated_completion: true, // Flag to know this was calculated
							};
						} catch (error) {
							console.error(
								"Error calculating completion for category:",
								courseProgress.category_id,
								error
							);
							return courseProgress;
						}
					})
				);

				// FIXED: Map progress by category_id correctly
				const progressMap = progressWithCalculatedCompletion?.reduce(
					(acc, progress) => {
						
						return {
							...acc,
							[progress.category_id]: progress,
						};
					},
					{}
				);


				setCategories(transformedCategories || []);
				setCourseProgress(progressMap || {});
			} catch (error) {
				console.error("Error fetching data:", error);
			} finally {
				setLoading(false);
			}
		};

		fetchData();
	}, [selectedGrade, user?.id, currentAcademicYear]);

	const handleViewCourse = (categoryId) => {
		navigate(`/game-data/${categoryId}`);
	};

	const handleStartCourse = async (categoryId) => {
		try {
			if (!courseProgress[categoryId]) {
				// First, get the current grade's parent grade
				const { data: currentGradeData, error: gradeError } = await supabase
					.from("grades")
					.select("parent_grade")
					.eq("id", selectedGrade)
					.single();

				if (gradeError) throw gradeError;

				const parentGrade = currentGradeData.parent_grade;

				// Get all grades that the teacher has access to
				const { data: teacherGrades, error: teacherGradesError } =
					await supabase
						.from("teacher_grades")
						.select("grade_id")
						.eq("profile_id", user.id);

				if (teacherGradesError) throw teacherGradesError;

				// Get all grades with the same parent grade that the teacher has access to
				const { data: relatedGrades, error: relatedGradesError } =
					await supabase
						.from("grades")
						.select("id")
						.eq("parent_grade", parentGrade)
						.in(
							"id",
							teacherGrades.map((tg) => tg.grade_id)
						);

				if (relatedGradesError) throw relatedGradesError;

				// Check if the category exists for each related grade
				const categoryCheckPromises = relatedGrades.map((grade) =>
					supabase
						.from("category_grades")
						.select("category_id")
						.eq("category_id", categoryId)
						.eq("grade_id", grade.id)
						.single()
				);

				const categoryCheckResults = await Promise.allSettled(
					categoryCheckPromises
				);

				// Filter grades where the category exists
				const validGrades = relatedGrades.filter(
					(grade, index) =>
						categoryCheckResults[index].status === "fulfilled" &&
						categoryCheckResults[index].value.data
				);

				// Create progress entries for all valid grades
				const progressEntries = validGrades.map((grade) => ({
					teacher_id: user.id,
					category_id: categoryId,
					grade_id: grade.id,
					status: "in_progress",
					completion_percentage: 0, // Start with 0%
					started_at: new Date().toISOString(),
					last_accessed_at: new Date().toISOString(),
					academic_year: currentAcademicYear,
				}));

				const { data, error } = await supabase
					.from("teacher_course_progress")
					.insert(progressEntries)
					.select();

				if (error) throw error;

				// FIXED: Update the courseProgress state with the progress for current grade only
				const currentGradeProgress = data.find(
					(p) => p.grade_id === selectedGrade
				);
				if (currentGradeProgress) {
					setCourseProgress((prev) => ({
						...prev,
						[categoryId]: currentGradeProgress,
					}));
				}

			}

			navigate(`/game-data/${categoryId}`);
		} catch (error) {
			console.error("Error starting course:", error);
		}
	};

	const getFilteredCategories = () => {
		return categories.filter((category) => {
			// First apply search filter
			const searchLower = searchQuery.toLowerCase();
			const matchesSearch =
				category.name?.toLowerCase().includes(searchLower) ||
				category.concept?.toLowerCase().includes(searchLower) ||
				category.description?.toLowerCase().includes(searchLower);

			if (!matchesSearch) return false;

			// Then apply status filter
			const progress = courseProgress[category.id];

			switch (statusFilter) {
				case "not_started":
					return !progress;
				case "in_progress":
					return (
						progress &&
						progress.completion_percentage !== null &&
						progress.completion_percentage < 100
					);
				case "completed":
					return progress && progress.completion_percentage === 100;
				default:
					return true;
			}
		});
	};

	const filteredCategories = getFilteredCategories();

	if (loading) {
		return (
			<Box sx={{ p: 2 }}>
				<Grid container spacing={2}>
					{[1, 2, 3, 4].map((item) => (
						<Grid item xs={12} sm={6} md={4} lg={3} key={item}>
							<Card>
								<Skeleton variant="rectangular" height={120} />
								<CardContent sx={{ py: 1 }}>
									<Skeleton variant="text" height={20} />
									<Skeleton variant="text" width="60%" />
								</CardContent>
							</Card>
						</Grid>
					))}
				</Grid>
			</Box>
		);
	}

	if (!selectedGrade) {
		return (
			<Box
				sx={{
					p: 2,
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					height: "200px",
				}}>
				<Typography variant="h6" color="text.secondary">
					Please select a grade to view available courses
				</Typography>
			</Box>
		);
	}

	return (
		<Box sx={{ p: 2 }}>
			{isMobile ? (
				// Mobile view - stacked layout
				<Stack spacing={2} sx={{ mb: 2 }}>
					{/* Header row */}
					<Box>
						<Typography variant="h6" sx={{ fontWeight: 600, color: "#111827" }}>
							Available Courses
						</Typography>
					</Box>

					{/* Status filter row (full width on mobile) */}
					<Box sx={{ width: "100%" }}>
						<Select
							fullWidth
							size="small"
							value={statusFilter}
							onChange={(e) => setStatusFilter(e.target.value)}
							sx={{
								height: 36,
								backgroundColor: "white",
								fontSize: "0.875rem",
								"& .MuiOutlinedInput-notchedOutline": {
									borderColor: "#e5e7eb",
								},
								"&:hover .MuiOutlinedInput-notchedOutline": {
									borderColor: "#6366f1",
								},
							}}>
							{STATUS_OPTIONS.map((option) => (
								<MenuItem
									key={option.value}
									value={option.value}
									sx={{ fontSize: "0.875rem" }}>
									{option.label}
								</MenuItem>
							))}
						</Select>
					</Box>

					{/* Search row (full width on mobile) */}
					<Box sx={{ width: "100%" }}>
						<TextField
							fullWidth
							size="small"
							placeholder="Search courses..."
							value={searchQuery}
							onChange={(e) => setSearchQuery(e.target.value)}
							InputProps={{
								startAdornment: (
									<InputAdornment position="start">
										<Search size={18} color="#6b7280" />
									</InputAdornment>
								),
								sx: {
									height: 36,
									backgroundColor: "white",
									"& fieldset": { borderColor: "#e5e7eb" },
									"&:hover fieldset": { borderColor: "#6366f1" },
								},
							}}
						/>
					</Box>
				</Stack>
			) : (
				// Desktop view - original layout
				<Box
					sx={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "center",
						mb: 2,
					}}>
					<Typography variant="h6" sx={{ fontWeight: 600, color: "#111827" }}>
						Available Courses
					</Typography>

					<Stack direction="row" spacing={2} alignItems="center">
						<Select
							size="small"
							value={statusFilter}
							onChange={(e) => setStatusFilter(e.target.value)}
							sx={{
								width: 150,
								height: 36,
								backgroundColor: "white",
								fontSize: "0.875rem",
								"& .MuiOutlinedInput-notchedOutline": {
									borderColor: "#e5e7eb",
								},
								"&:hover .MuiOutlinedInput-notchedOutline": {
									borderColor: "#6366f1",
								},
							}}>
							{STATUS_OPTIONS.map((option) => (
								<MenuItem
									key={option.value}
									value={option.value}
									sx={{ fontSize: "0.875rem" }}>
									{option.label}
								</MenuItem>
							))}
						</Select>

						<TextField
							size="small"
							placeholder="Search courses..."
							value={searchQuery}
							onChange={(e) => setSearchQuery(e.target.value)}
							InputProps={{
								startAdornment: (
									<InputAdornment position="start">
										<Search size={18} color="#6b7280" />
									</InputAdornment>
								),
								sx: {
									height: 36,
									backgroundColor: "white",
									"& fieldset": { borderColor: "#e5e7eb" },
									"&:hover fieldset": { borderColor: "#6366f1" },
								},
							}}
							sx={{ width: 240 }}
						/>
					</Stack>
				</Box>
			)}

			{filteredCategories.length === 0 ? (
				<Box
					sx={{
						p: 2,
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
						height: "120px",
					}}>
					<Typography variant="body1" color="text.secondary">
						No courses match your criteria
					</Typography>
				</Box>
			) : (
				<Grid container spacing={2}>
					{filteredCategories.map((category) => {
						const progress = courseProgress[category.id];
						const isStarted = !!progress;
						const isCompleted = progress?.completion_percentage === 100;
						const completionPercentage = progress?.completion_percentage || 0;

						

						return (
							<Grid item xs={12} sm={6} md={4} lg={3} key={category.id}>
								<Card sx={{ display: "flex", flexDirection: "column" }}>
									<CardMedia
										component="img"
										height="120"
										image={category.thumbnail || "/api/placeholder/400/320"}
										alt={category.name}
										sx={{
											objectFit: "cover",
											bgcolor: category.thumbnail ? "transparent" : "#f3f4f6",
										}}
									/>
									<CardContent
										sx={{ py: 1.5, px: 2, "&:last-child": { pb: 1.5 } }}>
										<Typography
											variant="subtitle1"
											sx={{
												fontWeight: 600,
												color: "#111827",
												mb: 0.5,
												fontSize: "0.9rem",
											}}>
											{category.name}
										</Typography>
										<Typography
											variant="body2"
											color="text.secondary"
											sx={{
												mb: isStarted ? 1 : 1.5,
												fontSize: "0.8rem",
												lineHeight: 1.4,
											}}>
											{category.concept ||
												category.description ||
												"Explore this course category"}
										</Typography>
										{isStarted && (
											<Box sx={{ mb: 1.5 }}>
												<LinearProgress
													variant="determinate"
													value={completionPercentage}
													sx={{
														height: 4,
														borderRadius: 1,
														bgcolor: "#e5e7eb",
														".MuiLinearProgress-bar": {
															bgcolor: "#6366f1",
														},
													}}
												/>
												<Typography
													variant="caption"
													color="text.secondary"
													sx={{
														mt: 0.5,
														display: "block",
														fontSize: "0.7rem",
													}}>
													{completionPercentage}% Complete
												</Typography>
											</Box>
										)}
										<Stack direction="row" spacing={1}>
											{isStarted && (
												<Button
													variant="outlined"
													size="small"
													startIcon={<BookOpen size={14} />}
													onClick={() => handleViewCourse(category.id)}
													sx={{
														flex: 1,
														color: "#6366f1",
														borderColor: "#6366f1",
														textTransform: "none",
														fontSize: "0.8rem",
														py: 0.5,
														"&:hover": {
															borderColor: "#4f46e5",
															backgroundColor: "rgba(99, 102, 241, 0.04)",
														},
													}}>
													View
												</Button>
											)}
											{isCompleted ? (
												<Typography
													variant="body2"
													sx={{
														flex: 1,
														color: "#059669",
														fontSize: "0.8rem",
														display: "flex",
														alignItems: "center",
														justifyContent: "center",
														bgcolor: "#ecfdf5",
														borderRadius: 1,
														py: 0.5,
														px: 2,
													}}>
													Completed
												</Typography>
											) : (
												<Button
													variant="contained"
													size="small"
													startIcon={<Play size={14} />}
													onClick={() => handleStartCourse(category.id)}
													sx={{
														flex: 1,
														backgroundColor: "#6366f1",
														textTransform: "none",
														fontSize: "0.8rem",
														py: 0.5,
														"&:hover": {
															backgroundColor: "#4f46e5",
														},
													}}>
													{isStarted ? "Continue" : "Start"}
												</Button>
											)}
										</Stack>
									</CardContent>
								</Card>
							</Grid>
						);
					})}
				</Grid>
			)}
		</Box>
	);
};

export default FunmathGames;
